import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Flex,
    Input,
    FormControl,
    FormErrorMessage,
    FormLabel,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import { FilterContainer } from "components/Tables/FilterContainer";
import { formatCPF, parseDateBrToISO } from "utils/utils";
import { LOCALE_PT_BR } from "constants/date-time";
import DateTime from "react-datetime";
import { plateMask, validateVehiclePlate } from "utils/forms-utils";

export const ParkingCredentialsFilter = (props) => {
    const { t } = useTranslation('common');
    const { onFilter, filterSnapshot } = props;
    const { isOpen, onToggle } = useDisclosure();
    const [plate, setPlate] = useState("");
    const [name, setName] = useState("");
    const [cpf, setCpf] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const isStartDateInvalid = startDate ? startDate > new Date() : false;
    const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
    const [plateError, setPlateError] = useState(null);
    
    const handlePlateChange = (e) => {
        const maskedPlate = plateMask(e.target.value);
        setPlate(maskedPlate);
        if (plateError) setPlateError(null);
    };

    const onFilterHandler = () => {
        onFilter({
            placa: plate,
            nome_beneficiario: name,
            cpf_beneficiario: cpf,
            data_inicial: startDate ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR) : "",
            data_final: endDate ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR) : "",
        });
    }

    const onClearHandler = () => {
        setPlate("");
        setName("");
        setCpf("");
        setStartDate("");
        setEndDate("");
        setPlateError(null);

        onFilter({
            placa: null,
            nome_beneficiario: null,
            cpf_beneficiario: null,
            data_inicial: null,
            data_final: null,
        });
    }

    useLayoutEffect(() => {
        if (filterSnapshot == null) {
            setPlate("");
            setName("");
            setCpf("");
            setStartDate("");
            setEndDate("");
            return;
        }
        setPlate(filterSnapshot.placa ? plateMask(String(filterSnapshot.placa)) : "");
        setName(filterSnapshot.nome_beneficiario ? String(filterSnapshot.nome_beneficiario) : "");
        setCpf(filterSnapshot.cpf_beneficiario ? formatCPF(String(filterSnapshot.cpf_beneficiario)) : "");
        if (filterSnapshot.data_inicial) {
            const d = parseDateBrToISO(filterSnapshot.data_inicial);
            setStartDate(d && !isNaN(d.getTime()) ? d : "");
        } else {
            setStartDate("");
        }
        if (filterSnapshot.data_final) {
            const d = parseDateBrToISO(filterSnapshot.data_final);
            setEndDate(d && !isNaN(d.getTime()) ? d : "");
        } else {
            setEndDate("");
        }
    }, [filterSnapshot]);

    const handleToggle = () => {
        if (isOpen) {
            setPlateError(null);
        }
        onToggle();
    };

    return (
        <FilterContainer
            w='125px'
            fontSize='sm'
            fontWeight='bold'
            variant="light"
            isOpen={isOpen}
            onToggle={handleToggle}
            onFilter={onFilterHandler}
            onClear={onClearHandler}
        >
            <Flex w="100%" gap="4" justify="space-between">
                <FormControl isInvalid={!!plateError}>
                    <FormLabel>
                        {t("parking-credentials.filters.plate.label")}
                    </FormLabel>
                    <Input
                        fontSize="sm"
                        placeholder={t("parking-credentials.filters.plate.placeholder")}
                        value={plate}
                        onBlur={() => {
                            const err = validateVehiclePlate(plate, t("parking-credentials.filters.plate.label"));
                            setPlateError(err);
                        }}
                        onChange={handlePlateChange}
                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                    />
                    <FormErrorMessage>
                        {plateError && t(plateError)}
                    </FormErrorMessage>
                </FormControl>

                <FormControl>
                    <FormLabel>
                        {t("parking-credentials.filters.cpf.label")}
                    </FormLabel>
                    <Input
                        fontSize="sm"
                        placeholder={t("parking-credentials.filters.cpf.placeholder")}
                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                        value={cpf}
                        onChange={e => {
                            const formattedCpf = formatCPF(e.currentTarget.value);
                            setCpf(formattedCpf);
                        }}
                    />
                </FormControl>
            </Flex>

            <FormControl>
                <FormLabel>
                    {t("parking-credentials.filters.name.label")}
                </FormLabel>
                <Input
                    fontSize="sm"
                    placeholder={t("parking-credentials.filters.name.placeholder")}
                    _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                    value={name}
                    onChange={e => setName(e.currentTarget.value)} />
            </FormControl>

            <Flex w="100%" gap="4" justify="space-between">
                <FormControl w="230px" isInvalid={isStartDateInvalid}>
                    <FormLabel>
                        {t("system.labels.start-date")}
                    </FormLabel>
                    <DateTime
                        locale={LOCALE_PT_BR}
                        closeOnSelect
                        timeFormat={false}
                        inputProps={startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                        renderInput={(props) => (
                          <Input
                            {...props}
                            fontSize="sm"
                            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                          />
                        )}
                        onChange={date => setStartDate(date)} />
                    <FormErrorMessage>
                        {t("system.messages.errors.invalid-start-date")}
                    </FormErrorMessage>
                </FormControl>

                <FormControl w="230px" isInvalid={isEndDateInvalid}>
                    <FormLabel>
                        {t("system.labels.end-date")}
                    </FormLabel>
                    <DateTime
                        locale={LOCALE_PT_BR}
                        closeOnSelect
                        timeFormat={false}
                        inputProps={endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                        renderInput={(props) => (
                          <Input
                            {...props}
                            fontSize="sm"
                            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                          />
                        )}
                        onChange={date => setEndDate(date)} />
                    <FormErrorMessage>
                        {t("system.messages.errors.invalid-end-date")}
                    </FormErrorMessage>
                </FormControl>
            </Flex>
        </FilterContainer>
    );
}